import React, { Component, ReactNode } from 'react';
import { captureError } from '@repo/monitoring';
import { Card } from './Card';
import { CardError } from './CardError';

interface Props {
  children: ReactNode;
  cardName?: string;
  cardVariant?: 'normal' | 'wrapper';
  cardClassName?: string;
  sentryContext?: Record<string, any>;
}

interface State {
  hasError: boolean;
}

/**
 * Card error boundary for Results Portal
 * Catches errors in card components and reports them to Sentry with card-specific context
 */
export class CardErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const cardName = this.props.cardName || 'Unknown Card';
    console.error(
      `CardErrorBoundary (${cardName}) caught an error:`,
      error,
      errorInfo,
    );

    // Report to Sentry using monitoring package utilities
    captureError(error, {
      level: 'error',
      tags: {
        error_boundary: 'card',
        card_name: cardName,
        component_type: 'card',
        card_error: 'true',
        app: 'results-portal',
      },
      contexts: {
        card: {
          name: cardName,
          ...this.props.sentryContext,
        },
        card_info: {
          card_name: cardName,
          error_source: 'CardErrorBoundary',
        },
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      fingerprint: ['card-error-boundary', cardName, error.message],
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card
          variant={this.props.cardVariant}
          className={this.props.cardClassName}
        >
          <CardError />
        </Card>
      );
    }

    return this.props.children;
  }
}
